/**
 * ProjectSiteDiary.jsx — Per-project aggregation of DPR data.
 *
 * Surfaces the granular DPR sub-sections (Weather · Manpower · Hindrance ·
 * Safety · QC · Plant & Machinery · RFIs · Work Hold · Rework) as
 * project-scoped roll-ups so the PM can review the whole site diary
 * without paging through every individual DPR.
 *
 * Sub-tabs: Overview · Weather · Manpower · Materials · Safety · QC ·
 *           Plant & Machinery · Hindrance · Work Hold · Rework · RFIs · Photos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CloudSun, Users, Boxes, ShieldCheck, ClipboardList, Truck, AlertTriangle,
  Pause, FileWarning, FileText, Camera, RefreshCw, Calendar,
} from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatDate } from '../../lib/format';

const SUB_TABS = [
  { k: 'overview',  label: 'Overview',         icon: ClipboardList },
  { k: 'weather',   label: 'Weather',          icon: CloudSun },
  { k: 'manpower',  label: 'Manpower',         icon: Users },
  { k: 'materials', label: 'Materials',        icon: Boxes },
  { k: 'safety',    label: 'Safety',           icon: ShieldCheck },
  { k: 'qc',        label: 'Quality / QC',     icon: ClipboardList },
  { k: 'plant',     label: 'Plant & Machinery', icon: Truck },
  { k: 'hindrance', label: 'Hindrances',       icon: AlertTriangle },
  { k: 'hold',      label: 'Work Hold',        icon: Pause },
  { k: 'rework',    label: 'Rework',           icon: FileWarning },
  { k: 'rfi',       label: 'RFIs',             icon: FileText },
  { k: 'photos',    label: 'Photos',           icon: Camera },
];

const MANPOWER_KEYS = [
  { k: 'engineers', label: 'Engr' },
  { k: 'supervisors', label: 'Sup' },
  { k: 'safety_officer', label: 'Safety' },
  { k: 'qa_qc', label: 'QA/QC' },
  { k: 'skilled', label: 'Skilled' },
  { k: 'unskilled', label: 'Unskilled' },
  { k: 'operators', label: 'Operator' },
];

export default function ProjectSiteDiary({ project }) {
  const [dprs, setDprs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState('overview');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      // full=1 returns complete docs including department_sections and weather
      const all = await apiGet('/dpr', { project_id: project.id, full: 1 });
      setDprs(Array.isArray(all) ? all : []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const filtered = useMemo(() => {
    let list = [...dprs].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    if (dateFrom) list = list.filter(d => (d.date || '') >= dateFrom);
    if (dateTo) list = list.filter(d => (d.date || '') <= dateTo);
    return list;
  }, [dprs, dateFrom, dateTo]);

  // Aggregations
  const stats = useMemo(() => {
    const s = {
      dprCount: filtered.length,
      latestDate: filtered[0]?.date || null,
      mpTotal: 0,
      hindranceCount: 0,
      hindranceHours: 0,
      reworkCount: 0,
      reworkCost: 0,
      holdCount: 0,
      activeFronts: 0,
      photoCount: 0,
      safetyManhoursTotal: 0,
      plantIdleCost: 0,
    };
    for (const d of filtered) {
      // Manpower from manpower block (server-computed when available, else from execution data)
      const mp = d.manpower || {};
      const mc = mp.skill_counts || {};
      s.mpTotal += Object.values(mc).reduce((acc, v) => acc + (Number(v) || 0), 0);

      const execData = d.department_sections?.execution?.data || {};
      const hs = execData.hindrances || d.hindrances || [];
      s.hindranceCount += hs.length;
      s.hindranceHours += hs.reduce((a, h) => a + (Number(h.impact_hours) || 0), 0);
      const rw = execData.rework_items || d.rework_items || [];
      s.reworkCount += rw.length;
      s.reworkCost += rw.reduce((a, r) => a + (Number(r.cost_impact) || 0), 0);
      s.holdCount += (execData.work_holds || d.work_holds || []).length;
      s.activeFronts += (execData.work_fronts || d.work_fronts || []).filter(f => (f.available || '') === 'Yes').length;
      s.photoCount += (d.photos || []).length;

      const safetyData = d.department_sections?.safety?.data || {};
      s.safetyManhoursTotal += Number(safetyData.total_man_hours || d.safety_manhours || 0);

      const procData = d.department_sections?.procurement?.data || {};
      const equipment = procData.equipment_running || d.plant_machinery || [];
      for (const pm of equipment) {
        s.plantIdleCost += (Number(pm.idle_hours) || 0) * (Number(pm.hire_cost_hr) || 0);
      }
    }
    return s;
  }, [filtered]);

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center">Loading site diary…</div>;
  if (!dprs.length) return (
    <div className="text-center py-12 bg-slate-50 border border-dashed border-slate-200 rounded-sm">
      <Calendar size={28} className="mx-auto text-slate-300 mb-2" />
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-1">No DPRs for this project</div>
      <div className="text-[11px] text-slate-500">Submit a DPR from the desktop "DPR" page or the mobile app's "Submit DPR" tile.</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="project-site-diary">
      {/* Sub-tab strip */}
      <div className="border-b border-slate-200 -mx-5 px-5 flex gap-1 overflow-x-auto">
        {SUB_TABS.map(t => (
          <button
            key={t.k}
            onClick={() => setSub(t.k)}
            data-testid={`diary-tab-${t.k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-display font-semibold whitespace-nowrap rounded-t-md ${sub === t.k ? 'bg-[#3b4872]/10 text-[#3b4872] border-b-2 border-[#3b4872]' : 'text-slate-500 hover:text-[#0F172A] border-b-2 border-transparent'}`}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* Date range + count */}
      <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
        <span className="kpi-overline">Range</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm bg-white" data-testid="diary-date-from" />
        <span className="text-slate-400">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm bg-white" data-testid="diary-date-to" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-slate-500 hover:text-rose-600 underline text-[11px]">Clear</button>
        )}
        <span className="ml-auto text-[11px]"><b>{filtered.length}</b> DPR{filtered.length === 1 ? '' : 's'} · last: <b>{stats.latestDate ? formatDate(stats.latestDate) : '—'}</b></span>
        <button onClick={load} className="text-slate-500 hover:text-[#3b4872] flex items-center gap-1 text-[11px]"><RefreshCw size={11} /> Refresh</button>
      </div>

      {/* ============ Overview ============ */}
      {sub === 'overview' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="diary-overview-grid">
          <KpiCard label="DPRs filed" value={stats.dprCount} />
          <KpiCard label="Total mandays" value={stats.mpTotal.toLocaleString('en-IN')} />
          <KpiCard label="Hindrance hours" value={stats.hindranceHours.toFixed(0)} accent={stats.hindranceHours > 50 ? 'border-rose-300' : 'border-amber-300'} suffix="h" />
          <KpiCard label="Rework items" value={stats.reworkCount} sub={stats.reworkCost > 0 ? `₹${stats.reworkCost.toLocaleString('en-IN')} impact` : null} accent={stats.reworkCount ? 'border-rose-300' : ''} />
          <KpiCard label="Work holds" value={stats.holdCount} accent={stats.holdCount ? 'border-rose-300' : ''} />
          <KpiCard label="Active work fronts" value={stats.activeFronts} accent="border-emerald-300" />
          <KpiCard label="Safety m-hrs" value={stats.safetyManhoursTotal.toLocaleString('en-IN')} accent="border-emerald-300" />
          <KpiCard label="Plant idle cost" value={`₹${stats.plantIdleCost.toLocaleString('en-IN')}`} accent={stats.plantIdleCost > 0 ? 'border-rose-300' : ''} />
          <KpiCard label="Photos uploaded" value={stats.photoCount} />
        </div>
      )}

      {/* ============ Weather ============ */}
      {sub === 'weather' && (
        <DataTable
          rows={filtered}
          cols={[
            { k: 'date',    label: 'Date',     render: r => formatDate(r.date) },
            { k: 'morning', label: 'Morning',  render: r => r.weather?.morning || '—' },
            { k: 'aft',     label: 'Afternoon',render: r => r.weather?.afternoon || '—' },
            { k: 'eve',     label: 'Evening',  render: r => r.weather?.evening || '—' },
            { k: 'rain',    label: 'Rain mm',  render: r => r.weather?.rainfall_mm ?? '—' },
            { k: 'max_t',   label: 'Max °C',   render: r => r.weather?.max_temp_c ?? '—' },
            { k: 'min_t',   label: 'Min °C',   render: r => r.weather?.min_temp_c ?? '—' },
            { k: 'notes',   label: 'Notes',    render: r => r.weather?.weather_notes || '—' },
          ]}
        />
      )}

      {/* ============ Manpower ============ */}
      {sub === 'manpower' && (
        <DataTable
          rows={filtered}
          cols={[
            { k: 'date', label: 'Date', render: r => formatDate(r.date) },
            ...MANPOWER_KEYS.map(c => ({
              k: c.k, label: c.label,
              render: r => (r.manpower?.skill_counts || {})[c.k] ?? (r.manpower_count || {})[c.k] ?? 0,
            })),
            { k: 'total', label: 'Total', render: r => {
              const sc = r.manpower?.skill_counts || r.manpower_count || {};
              return <b>{Object.values(sc).reduce((s, v) => s + (Number(v) || 0), 0)}</b>;
            }},
            { k: 'lab_split', label: 'Total Present (Attendance)', render: r =>
              r.manpower?.total_present_attendance ?? r.manpower?.skill_total ?? '—'
            },
          ]}
        />
      )}

      {/* ============ Materials ============ */}
      {sub === 'materials' && (
        <div className="space-y-4">
          <SectionList title="Materials Received" emptyMsg="No materials received recorded"
            rows={filtered.flatMap(d => (d.department_sections?.stores?.data?.materials_received || []).map(s => ({ date: d.date, filled_by: d.department_sections?.stores?.filled_by, ...s })))}
            cols={[
              { k: 'date', label: 'Date', render: r => formatDate(r.date) },
              { k: 'description', label: 'Material' },
              { k: 'qty', label: 'Qty' },
              { k: 'unit', label: 'Unit' },
              { k: 'supplier', label: 'Supplier', render: r => r.supplier || '—' },
              { k: 'filled_by', label: 'By', render: r => r.filled_by || '—' },
            ]}
          />
          <SectionList title="Materials Issued" emptyMsg="No materials issued recorded"
            rows={filtered.flatMap(d => (d.department_sections?.stores?.data?.materials_issued || []).map(s => ({ date: d.date, filled_by: d.department_sections?.stores?.filled_by, ...s })))}
            cols={[
              { k: 'date', label: 'Date', render: r => formatDate(r.date) },
              { k: 'description', label: 'Material' },
              { k: 'qty', label: 'Qty' },
              { k: 'unit', label: 'Unit' },
              { k: 'issued_to', label: 'Issued To', render: r => r.issued_to || '—' },
              { k: 'filled_by', label: 'By', render: r => r.filled_by || '—' },
            ]}
          />
          <SectionList title="Procurement Required (Next Day)" emptyMsg="No next-day procurement requests"
            rows={filtered.flatMap(d => (d.procurement_next_day || []).map(s => ({ date: d.date, ...s })))}
            cols={[
              { k: 'date', label: 'Raised', render: r => formatDate(r.date) },
              { k: 'material', label: 'Material' },
              { k: 'spec', label: 'Spec' },
              { k: 'qty_required', label: 'Req' },
              { k: 'available_stock', label: 'Stock' },
              { k: 'qty_to_procure', label: 'To Procure' },
              { k: 'vendor', label: 'Vendor' },
            ]}
          />
          <SectionList title="Consumption vs BOQ" emptyMsg="No BOQ tracking entries"
            rows={filtered.flatMap(d => (d.boq_consumption || []).map(s => ({ date: d.date, ...s })))}
            cols={[
              { k: 'date', label: 'Date', render: r => formatDate(r.date) },
              { k: 'material', label: 'Material' },
              { k: 'unit', label: 'Unit' },
              { k: 'boq_qty', label: 'BOQ Qty' },
              { k: 'cumulative_consumed', label: 'Cum Used' },
              { k: 'balance', label: 'Balance', render: r => {
                const b = (Number(r.boq_qty) || 0) - (Number(r.cumulative_consumed) || 0);
                return <b className={b < 0 ? 'text-rose-700' : ''}>{b}</b>;
              }},
              { k: 'pct', label: '%', render: r => {
                const b = Number(r.boq_qty) || 0;
                return b > 0 ? Math.round((Number(r.cumulative_consumed || 0) / b) * 100) + '%' : '—';
              }},
            ]}
          />
        </div>
      )}

      {/* ============ Safety ============ */}
      {sub === 'safety' && (
        <DataTable
          rows={filtered.filter(d => d.department_sections?.safety?.data)}
          cols={[
            { k: 'date',       label: 'Date',          render: r => formatDate(r.date) },
            { k: 'toolbox',    label: 'Toolbox Talks', render: r => r.department_sections?.safety?.data?.toolbox_talks ?? '—' },
            { k: 'man_hrs',    label: 'M-hrs Today',   render: r => r.department_sections?.safety?.data?.total_man_hours ?? '—' },
            { k: 'incidents',  label: 'Incidents',     render: r => {
              const v = r.department_sections?.safety?.data?.incidents;
              return v != null ? <b className={Number(v) > 0 ? 'text-rose-700' : ''}>{v}</b> : '—';
            }},
            { k: 'ppe_viol',   label: 'PPE Violations',render: r => r.department_sections?.safety?.data?.ppe_violations ?? '—' },
            { k: 'ppe_comp',   label: 'PPE Compliance',render: r => {
              const v = r.department_sections?.safety?.data?.ppe_compliance_pct;
              return v != null ? `${v}%` : '—';
            }},
            { k: 'notes',      label: 'Notes',         render: r => r.department_sections?.safety?.data?.safety_notes || '—' },
            { k: 'filled_by',  label: 'By',            render: r => r.department_sections?.safety?.filled_by || '—' },
          ]}
        />
      )}

      {/* ============ Quality / QC ============ */}
      {sub === 'qc' && (
        <DataTable
          rows={filtered.filter(d => d.department_sections?.qa_qc?.data)}
          cols={[
            { k: 'date',      label: 'Date',        render: r => formatDate(r.date) },
            { k: 'insp_c',    label: 'Inspections', render: r => r.department_sections?.qa_qc?.data?.inspections_conducted ?? '—' },
            { k: 'insp_p',    label: 'Passed',      render: r => r.department_sections?.qa_qc?.data?.inspections_passed ?? '—' },
            { k: 'ncrs',      label: 'NCRs Raised', render: r => {
              const v = r.department_sections?.qa_qc?.data?.ncrs_raised;
              return v != null ? <b className={Number(v) > 0 ? 'text-rose-700' : ''}>{v}</b> : '—';
            }},
            { k: 'test_sum',  label: 'Test Results',render: r => r.department_sections?.qa_qc?.data?.test_results_summary || '—' },
            { k: 'qa_notes',  label: 'Notes',       render: r => r.department_sections?.qa_qc?.data?.qa_notes || '—' },
            { k: 'filled_by', label: 'By',          render: r => r.department_sections?.qa_qc?.filled_by || '—' },
          ]}
        />
      )}

      {/* ============ Plant & Machinery ============ */}
      {sub === 'plant' && (
        <SectionList title="Plant & Machinery Utilisation" emptyMsg="No plant entries recorded"
          rows={filtered.flatMap(d => {
            const eq = d.department_sections?.procurement?.data?.equipment_running
              || d.plant_machinery || [];
            return eq.map(s => ({ date: d.date, ...s }));
          })}
          cols={[
            { k: 'date',          label: 'Date',    render: r => formatDate(r.date) },
            { k: 'name',          label: 'Equipment', render: r => r.name || r.equipment || r.item || '—' },
            { k: 'running_hours', label: 'Working',   render: r => r.running_hours ?? r.working_hours ?? '—' },
            { k: 'idle_hours',    label: 'Idle',      render: r => (r.idle_hours != null && r.idle_hours !== 0) ? <b className="text-rose-700">{r.idle_hours}h</b> : '—' },
            { k: 'po_no',         label: 'PO No.',    render: r => r.po_no || '—' },
            { k: 'remarks',       label: 'Remarks',   render: r => r.remarks || '—' },
          ]}
        />
      )}

      {/* ============ Hindrance ============ */}
      {sub === 'hindrance' && (
        <SectionList title="Hindrance Log" emptyMsg="No hindrances recorded — site running clean!"
          rows={filtered.flatMap(d => {
            const hs = d.department_sections?.execution?.data?.hindrances || d.hindrances || [];
            return hs.map(s => ({ date: d.date, ...s }));
          })}
          cols={[
            { k: 'date', label: 'Date', render: r => formatDate(r.date) },
            { k: 'category', label: 'Category', render: r => r.category ? <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{r.category}</span> : '—' },
            { k: 'description', label: 'Description' },
            { k: 'impact_hours', label: 'Hrs Lost', render: r => r.impact_hours ? <b className="text-amber-700">{r.impact_hours}</b> : '—' },
            { k: 'remarks', label: 'Action Taken', render: r => r.remarks || r.action_taken || '—' },
          ]}
        />
      )}

      {/* ============ Work Hold ============ */}
      {sub === 'hold' && (
        <SectionList title="Work Hold / Stoppage from Client" emptyMsg="No client-side holds recorded"
          rows={filtered.flatMap(d => {
            const wh = d.department_sections?.execution?.data?.work_holds || d.work_holds || [];
            return wh.map(s => ({ date: d.date, ...s }));
          })}
          cols={[
            { k: 'date', label: 'Logged', render: r => formatDate(r.date) },
            { k: 'ref', label: 'Hold Ref' },
            { k: 'client', label: 'Client' },
            { k: 'reason', label: 'Reason' },
            { k: 'from_date', label: 'From' },
            { k: 'to_date', label: 'To' },
            { k: 'remarks', label: 'Remarks' },
          ]}
        />
      )}

      {/* ============ Rework ============ */}
      {sub === 'rework' && (
        <SectionList title="Rework Details" emptyMsg="No rework recorded"
          rows={filtered.flatMap(d => {
            const rw = d.department_sections?.execution?.data?.rework_items || d.rework_items || [];
            return rw.map(s => ({ date: d.date, ...s }));
          })}
          cols={[
            { k: 'date', label: 'Date', render: r => formatDate(r.date) },
            { k: 'location', label: 'Location' },
            { k: 'reason', label: 'Reason' },
            { k: 'responsibility', label: 'Resp' },
            { k: 'qty', label: 'Qty' },
            { k: 'cost_impact', label: 'Cost ₹', render: r => r.cost_impact ? `₹${Number(r.cost_impact).toLocaleString('en-IN')}` : '—' },
            { k: 'time_impact_days', label: 'Days' },
            { k: 'approval_ref', label: 'Approval' },
          ]}
        />
      )}

      {/* ============ RFIs ============ */}
      {sub === 'rfi' && (
        <SectionList title="RFIs Raised (per DPR)" emptyMsg="No RFIs raised through DPR yet"
          rows={filtered.filter(d => {
            const rfis = d.department_sections?.execution?.data?.rfis_raised || d.rfis_raised;
            return rfis != null && rfis !== '';
          }).map(d => ({
            date: d.date,
            rfis_raised: d.department_sections?.execution?.data?.rfis_raised || d.rfis_raised,
            submitted_by: d.department_sections?.execution?.filled_by || d.submitted_by,
          }))}
          cols={[
            { k: 'date', label: 'Date', render: r => formatDate(r.date) },
            { k: 'rfis_raised', label: 'RFI No(s)' },
            { k: 'submitted_by', label: 'By' },
          ]}
        />
      )}

      {/* ============ Photos ============ */}
      {sub === 'photos' && (
        <div data-testid="diary-photos">
          {filtered.flatMap(d => (d.photos || []).map(p => ({ date: d.date, ...p }))).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">No site photos uploaded via DPR.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {filtered.flatMap(d => (d.photos || []).map(p => ({ date: d.date, ...p }))).slice(0, 60).map((p, i) => (
                <div key={i} className="bg-slate-100 border border-slate-200 rounded-sm aspect-square flex items-center justify-center text-[10px] text-slate-400 relative">
                  <Camera size={20} />
                  <span className="absolute bottom-1 left-1 bg-black/60 text-white px-1 py-0.5 rounded text-[9px]">{formatDate(p.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ label, value, sub, accent, suffix }) => (
  <div className={`bg-white border ${accent || 'border-slate-200'} rounded-sm p-3`}>
    <div className="kpi-overline">{label}</div>
    <div className="font-display font-bold text-xl mt-0.5 tabular-nums">{value}{suffix && <span className="text-xs text-slate-400 ml-1">{suffix}</span>}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const StatusChip = ({ s }) => {
  if (!s) return <span className="text-slate-400">—</span>;
  const cls = ['Yes', 'Approved'].includes(s) ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : ['No', 'Rejected'].includes(s)  ? 'bg-rose-50 text-rose-700 border-rose-200'
            : ['Pending'].includes(s)         ? 'bg-amber-50 text-amber-700 border-amber-200'
                                              : 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>{s}</span>;
};

const SectionList = ({ title, rows, cols, emptyMsg }) => (
  <div>
    <div className="kpi-overline mb-1.5">{title} {rows.length > 0 && <span className="text-slate-400">· {rows.length}</span>}</div>
    {rows.length === 0
      ? <div className="text-xs text-slate-400 italic py-3 text-center bg-slate-50 border border-dashed border-slate-200 rounded-sm">{emptyMsg}</div>
      : <DataTable rows={rows} cols={cols} />}
  </div>
);

const DataTable = ({ rows, cols }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-sm">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          {cols.map(c => (
            <th key={c.k} className="text-left py-2 px-3 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 100).map((r, i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
            {cols.map(c => (
              <td key={c.k} className="py-1.5 px-3 align-top">{c.render ? c.render(r) : (r[c.k] ?? '—')}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length > 100 && (
      <div className="text-center py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
        Showing first 100 of {rows.length} rows
      </div>
    )}
  </div>
);
